import {animate, AnimationBuilder, AnimationMetadata, AnimationPlayer, style} from '@angular/animations';
import {ChangeDetectorRef, Directive, EmbeddedViewRef, Input, TemplateRef, ViewContainerRef} from '@angular/core';
import {BehaviorSubject, Observable, Observer} from 'rxjs';
import {delay, filter, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../destroyable';

interface FlashMessageOfContext<T> {
    $implicit: T;
}

interface FlashMessageOfOptions {
    activeTime: number; // Don't include both fade in/out timings.
    fadeInTiming: number | string; // See doc for angular's animate method.
    fadeOutTiming: number | string; // See doc for angular's animate method.
}

interface AnimationDef {
    name: string; // For debug purposes.
    animationMetadata: AnimationMetadata | AnimationMetadata[];
}

@Directive({
    selector: '[flashMessage][flashMessageOf]', // Used the ng's micro-syntax like in ngForOf (*ngFor="...of...").
    standalone: false
})
export class FlashMessageOfDirective<T = any> {

    @Input()
    public set flashMessageOf(stream$: Observable<T>) {
        this.streamValue$.next(stream$);
    }

    @Input()
    public set flashMessageOptions(inputOptions: Partial<FlashMessageOfOptions>) {
        this.options = {
            ...this.defaultOptions,
            ...(inputOptions || {})
        };
    }

    public readonly destroy$ = destroy$();

    private viewRef: EmbeddedViewRef<FlashMessageOfContext<T>>;
    private viewElement: HTMLElement;

    private readonly streamValue$ = new BehaviorSubject<Observable<T>>(null);

    private readonly defaultOptions: FlashMessageOfOptions = {
        activeTime:    5000,
        fadeInTiming:  '300ms ease-in',
        fadeOutTiming: '500ms ease-out'
    };
    private options: FlashMessageOfOptions = {...this.defaultOptions};

    constructor(
        private templateRef: TemplateRef<FlashMessageOfContext<T>>,
        private viewContainerRef: ViewContainerRef,
        private animationBuilder: AnimationBuilder,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        this.streamValue$.pipe(
            filter(stream$ => !!stream$),
            switchMap(stream$ => stream$),
            switchMap(notification => this.createNotificationLifecycle(notification)),
            finalize(() => this.remove()),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private create(notification: T): void {
        if (!this.viewRef) {
            const context: FlashMessageOfContext<T> = {
                $implicit: notification
            };

            this.viewRef = this.viewContainerRef.createEmbeddedView(this.templateRef, context);
            this.viewElement = this.viewRef.rootNodes.find((element: HTMLElement) => !!element.tagName);

            this.changeDetectorRef.markForCheck();
        }
    }

    private remove(): void {
        if (this.viewRef) {
            const viewRefIndex: number = this.viewContainerRef.indexOf(this.viewRef);

            if (viewRefIndex >= 0) {
                this.viewContainerRef.remove(viewRefIndex);
            }

            this.viewRef = null;
            this.viewElement = null;

            this.changeDetectorRef.markForCheck();
        }
    }

    private getFadeInAnimation(): AnimationDef {
        return {
            name: 'fadeIn',
            animationMetadata: [
                style({opacity: 0}),
                animate(this.options.fadeInTiming, style({opacity: 1}))
            ]
        };
    }

    private getFadeOutAnimation(): AnimationDef {
        return {
            name: 'fadeOut',
            animationMetadata: [
                style({opacity: 1}),
                animate(this.options.fadeOutTiming, style({opacity: 0}))
            ]
        };
    }

    private playAnimation(animation: AnimationDef): Observable<any> {
        return new Observable((observer: Observer<any>) => {
            let player: AnimationPlayer = this.animationBuilder.build(animation.animationMetadata)
                .create(this.viewElement);

            const destroyPlayer = () => {
                if (player) {
                    player.destroy();
                    player = null;
                }
            };

            player.onDone(() => {
                destroyPlayer();
                observer.next(null);
                observer.complete();
            });
            player.onDestroy(() => observer.complete());

            player.play();

            return destroyPlayer;
        });
    }

    private createNotificationLifecycle(notification: T): Observable<any> {
        this.remove(); // Remove previous notification if any.
        this.create(notification);

        return this.playAnimation(this.getFadeInAnimation()).pipe(
            delay(this.options.activeTime),
            switchMap(() => this.playAnimation(this.getFadeOutAnimation())),
            tap(() => this.remove())
        );
    }

}
